import { useState } from "react";
import { Card, Table, Button, Modal, Form, Input, message, Popconfirm, Space } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, Setting } from "../api";

export default function SystemSettings() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get<{ items: Setting[] }>("/settings")).data.items,
  });

  const upsert = useMutation({
    mutationFn: (v: { key: string; value: any; description?: string }) => api.post("/settings", v),
    onSuccess: () => { message.success("已保存"); qc.invalidateQueries({ queryKey: ["settings"] }); setOpen(false); setEditingKey(null); form.resetFields(); },
    onError: (e: any) => message.error(e?.response?.data?.error ?? "保存失败"),
  });

  const del = useMutation({
    mutationFn: (key: string) => api.delete(`/settings/${encodeURIComponent(key)}`),
    onSuccess: () => { message.success("已删除"); qc.invalidateQueries({ queryKey: ["settings"] }); },
    onError: (e: any) => message.error(e?.response?.data?.error ?? "删除失败"),
  });

  const openUpdateTemplate = (platform: "android" | "ios") => {
    const key = `app.update.${platform}`;
    const current = data?.find((item) => item.key === key);
    setEditingKey(current?.key ?? null);
    form.setFieldsValue({
      key,
      value: JSON.stringify(current?.value ?? {
        latest_version: "1.0.8",
        latest_build: 9,
        min_build: 1,
        download_url: platform === "android" ? "https://wwbou.lanzn.com/EldadZrakJfgNjLpOx8E" : "",
        release_notes: "1.0.8：新增 Solana 一等链支持和 DApp 确认预览；聊天视频支持封面预览、录制发送、保存和更宽松的视频限制；动态行情新增链上行情榜；新增日语、韩语国际化；优化白天/黑夜主题、空状态插图和代币 logo；修复群红包领取、DApp 浏览器兼容和过期红包退款异常。",
      }, null, 2),
      description: current?.description ?? `${platform === "android" ? "Android" : "iOS"} App 更新配置`,
    });
    setOpen(true);
  };

  const openRedPacketAssetsTemplate = () => {
    const key = "redpacket.assets";
    const current = data?.find((item) => item.key === key);
    setEditingKey(current?.key ?? null);
    form.setFieldsValue({
      key,
      value: JSON.stringify(current?.value ?? [
        {
          chain: "bsc",
          symbol: "BNB",
          name: "BNB",
          token_address: "0x0000000000000000000000000000000000000000",
          decimals: 18,
          contract_address: "0xYourRedPacketContract",
          enabled: true,
        },
        {
          chain: "bsc",
          symbol: "USDT",
          name: "Tether USD",
          token_address: "0x55d398326f99059fF775485246999027B3197955",
          decimals: 18,
          contract_address: "0xYourRedPacketContract",
          enabled: true,
        },
        {
          chain: "bsc",
          symbol: "QVX",
          name: "Quorvex",
          token_address: "0xa395999b8f96efbc0ad1cc97edc85304ca3632ef",
          decimals: 18,
          contract_address: "0xYourRedPacketContract",
          enabled: true,
        },
      ], null, 2),
      description: current?.description ?? "客户端红包可选资产列表",
    });
    setOpen(true);
  };

  return (
    <Card
      style={{ background: "#10131A", border: "1px solid #1A1D26" }}
      title={<span style={{ color: "#F5F5F7" }}>系统配置</span>}
      extra={
        <Space>
          <Button onClick={() => openUpdateTemplate("android")}>Android 版本</Button>
          <Button onClick={() => openUpdateTemplate("ios")}>iOS 版本</Button>
          <Button onClick={openRedPacketAssetsTemplate}>红包资产</Button>
          <Button type="primary" onClick={() => { setEditingKey(null); form.resetFields(); setOpen(true); }}>新增</Button>
        </Space>
      }
    >
      <Table<Setting>
        rowKey="key"
        loading={isLoading}
        dataSource={data}
        pagination={false}
        columns={[
          { title: "Key", dataIndex: "key", width: 240, render: (v: string) => <span style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: "#9B87F5" }}>{v}</span> },
          { title: "值", dataIndex: "value", render: (v) => <pre style={{ margin: 0, fontSize: 12, color: "#A1A1AA", whiteSpace: "pre-wrap" }}>{JSON.stringify(v)}</pre> },
          { title: "描述", dataIndex: "description", render: (v) => v ?? <span style={{ color: "#6B7280" }}>-</span> },
          { title: "更新时间", dataIndex: "updated_at", width: 180, render: (v) => new Date(v).toLocaleString() },
          {
            title: "操作", width: 130,
            render: (_, r) => (
              <>
                <Button size="small" style={{ marginRight: 8 }} onClick={() => { setEditingKey(r.key); form.setFieldsValue({ key: r.key, value: JSON.stringify(r.value), description: r.description ?? "" }); setOpen(true); }}>编辑</Button>
                <Popconfirm title="确认删除?" onConfirm={() => del.mutate(r.key)}>
                  <Button size="small" danger>删除</Button>
                </Popconfirm>
              </>
            ),
          },
        ]}
      />
      <Modal title={editingKey ? `编辑 ${editingKey}` : "新增配置"} open={open} onCancel={() => { setOpen(false); setEditingKey(null); }} confirmLoading={upsert.isPending}
        onOk={async () => {
          const v = await form.validateFields();
          let parsed: any;
          try { parsed = JSON.parse(v.value); } catch { message.error("值必须是合法 JSON"); return; }
          upsert.mutate({ key: v.key, value: parsed, description: v.description });
        }}>
        <Form form={form} layout="vertical">
          <Form.Item name="key" label="Key" rules={[{ required: true }]}><Input disabled={!!editingKey} placeholder="group.max_members" /></Form.Item>
          <Form.Item name="value" label="值 (JSON)" rules={[{ required: true }]} extra='字符串需带引号 ("hello"),数字布尔直接写 (1000 / true)'><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="description" label="描述"><Input /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
